import { CommonProfileService } from "subModule/src/core/services";
import { APP_BASE_URL } from "subModule/src/constant/Config";
import { axiosClient } from "subModule/src/core/axios";

/** Strip quotes / encoded quotes so imageUrl never becomes `file.png'`. */
export const sanitizeMediaFileName = (name: string): string =>
  String(name || "")
    .trim()
    .replace(/%27/gi, "")
    .replace(/%22/gi, "")
    .replace(/^['"]+|['"]+$/g, "")
    .trim();

export const extractMediaFileName = (urlOrName: string): string => {
  if (!urlOrName) return "";
  if (urlOrName.startsWith("data:") || urlOrName.startsWith("blob:")) return "";
  let name = "";
  try {
    name = new URL(urlOrName).pathname.split("/").pop() || "";
  } catch {
    name = urlOrName.split("?")[0].split("/").pop() || "";
  }
  return sanitizeMediaFileName(name);
};

export const getMimeTypeFromFilename = (filename: string) => {
  const clean = filename.split("?")[0].toLowerCase();
  if (clean.endsWith(".png")) return "image/png";
  if (clean.endsWith(".jpg") || clean.endsWith(".jpeg")) return "image/jpeg";
  if (clean.endsWith(".webp")) return "image/webp";
  if (clean.endsWith(".gif")) return "image/gif";
  if (clean.endsWith(".bmp")) return "image/bmp";
  if (clean.endsWith(".svg")) return "image/svg+xml";
  if (clean.endsWith(".ico")) return "image/x-icon";
  return "image/png";
};

export const normalizeToDataUrl = (
  base64OrDataUrl: string,
  filenameForMime: string,
) => {
  const trimmed = (base64OrDataUrl || "").trim().replace(/^["']|["']$/g, "");
  if (!trimmed) return "";
  if (trimmed.startsWith("data:")) return trimmed;
  if (trimmed.startsWith("{") || trimmed.startsWith("[")) return "";
  const mime = getMimeTypeFromFilename(filenameForMime);
  return `data:${mime};base64,${trimmed}`;
};

export const isImageFileName = (urlOrName: string) =>
  /\.(jpe?g|png|gif|webp|bmp|svg|tiff|ico)(\?.*)?$/i.test(
    (urlOrName || "").split("?")[0],
  );

/**
 * Browser-native media URL (no JS download). Prefer fetchMediaAsObjectUrl when
 * the API returns a text data-URL body instead of binary image bytes.
 */
export function getMediaViewUrl(urlOrName: string): string {
  if (!urlOrName) return "";
  if (
    urlOrName.startsWith("data:") ||
    urlOrName.startsWith("blob:") ||
    urlOrName.startsWith("http://") ||
    urlOrName.startsWith("https://")
  ) {
    return urlOrName;
  }
  const imageUrl = extractMediaFileName(urlOrName);
  if (!imageUrl) return "";
  const base = (APP_BASE_URL || "").replace(/\/$/, "");
  return `${base}/api/media/view?imageUrl=${encodeURIComponent(imageUrl)}`;
}

const dataUrlToObjectUrl = async (dataUrl: string): Promise<string> => {
  if (!dataUrl) return "";
  try {
    const res = await fetch(dataUrl);
    const blob = await res.blob();
    return URL.createObjectURL(blob);
  } catch {
    return dataUrl;
  }
};

/**
 * Fast display path: load media as a blob: object URL.
 * Tries binary image first; falls back to Postman-style data-URL text.
 */
export async function fetchMediaAsObjectUrl(
  urlOrName: string,
): Promise<string> {
  const imageUrl = extractMediaFileName(urlOrName);
  if (!imageUrl) return "";
  const base = (APP_BASE_URL || "").replace(/\/$/, "");
  const endpoint = `${base}/api/media/view`;

  try {
    const blobRes = await axiosClient.get<Blob>(endpoint, {
      params: { imageUrl },
      headers: { Accept: "image/*,*/*" },
      responseType: "blob",
    });
    const blob = blobRes.data;
    if (blob && typeof blob === "object" && blob.type?.startsWith("image/")) {
      return URL.createObjectURL(blob);
    }
    // API returned text data-URL inside a blob
    const text = await blob.text();
    const dataUrl = normalizeToDataUrl(text, imageUrl);
    return dataUrl ? await dataUrlToObjectUrl(dataUrl) : "";
  } catch {
    try {
      const res = await CommonProfileService.convertFilenameToBase64({
        imageUrl,
      });
      const raw = typeof res === "string" ? res : "";
      const dataUrl = normalizeToDataUrl(raw, imageUrl);
      return dataUrl ? await dataUrlToObjectUrl(dataUrl) : "";
    } catch {
      return "";
    }
  }
}

/**
 * Fetches media as a data URL (for previews/canvas/pdf).
 */
export async function fetchMediaAsDataUrl(
  urlOrName: string,
): Promise<string> {
  const imageUrl = extractMediaFileName(urlOrName);
  if (!imageUrl) return "";
  try {
    const res = await CommonProfileService.convertFilenameToBase64({
      imageUrl,
    });
    const raw = typeof res === "string" ? res : "";
    return normalizeToDataUrl(raw, imageUrl);
  } catch {
    return "";
  }
}

export async function fetchMediaAsDataUrlMap(
  urls: string[],
): Promise<Record<string, string>> {
  const entries = await Promise.all(
    (urls || []).map(async (url) => {
      if (!url || !isImageFileName(url)) return [url, ""] as const;
      const dataUrl = await fetchMediaAsDataUrl(url);
      return [url, dataUrl] as const;
    }),
  );
  return Object.fromEntries(entries.filter(([, src]) => !!src));
}
